import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef } from "react";
import { useRegion } from "@/context/RegionContext";
import { PHASE_TIMES, PX_PER_UNIT } from "@/i18n/hiw-data";
import type { PlaneSpec, HiwNotifSpec } from "@/i18n/types";
import { locumColors } from "@/theme";

gsap.registerPlugin(ScrollTrigger);

/* ── Constants ── */
const PREFS_CARD_WIDTH = 360;
const PHONE_WIDTH = 280;
const PLANE_GROUP_WIDTH = 280;
const PLANE_GAP = 32;
const GRID_COLS = 4;
const GRID_ROWS = 3;
const GRID_CELLS = 12;
const GRID_ORDER = [0, 3, 8, 11, 5, 6, 1, 10, 2, 9, 4, 7];
const STACK_TOP = 0.34;
const STACK_BOT = 0.94;
const STEPPER_LABELS = ["hiw-step1", "hiw-step2", "hiw-step3", "hiw-step4", "hiw-step5"];
const PHASE_HEADINGS = ["hiw-ph0-h", "hiw-ph1-h", "hiw-ph2-h", "hiw-ph3-h", "hiw-ph4-h"];
const PHASE_SUBS = ["hiw-ph0-sub", "hiw-ph1-sub", "hiw-ph2-sub", "hiw-ph3-sub", "hiw-ph4-sub"];

/* ── Layout helpers ── */
function hashRand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

function seededRange(seed: number, min: number, max: number): number {
  return min + hashRand(seed) * (max - min);
}

function chaosZone(vw: number, vh: number) {
  return { left: vw * 0.04, right: vw * 0.96, top: vh * 0.36, bottom: vh * 0.93 };
}

function stickyHeaderZone(_vw: number, vh: number) {
  return { left: 0, right: _vw, top: 0, bottom: Math.max(340, vh * 0.36) };
}

function avoidHero(seedBase: number, itemW: number, itemH: number, vw: number, vh: number) {
  const zone = chaosZone(vw, vh);
  const header = stickyHeaderZone(vw, vh);
  const effTop = Math.max(zone.top, header.bottom);
  const effBottom = zone.bottom;
  const effLeft = zone.left;
  const effRight = zone.right;
  const cellW = (effRight - effLeft) / GRID_COLS;
  const cellH = (effBottom - effTop) / GRID_ROWS;
  const insetX = Math.min(12, Math.max(0, (cellW - itemW) * 0.5));
  const insetY = Math.min(12, Math.max(0, (cellH - itemH) * 0.5));
  const cellIdx = GRID_ORDER[seedBase % GRID_CELLS] ?? seedBase % GRID_CELLS;
  const col = cellIdx % GRID_COLS;
  const row = Math.floor(cellIdx / GRID_COLS);
  const cellX1 = effLeft + col * cellW + insetX;
  const cellY1 = effTop + row * cellH + insetY;
  const cellX2 = effLeft + (col + 1) * cellW - insetX - itemW;
  const cellY2 = effTop + (row + 1) * cellH - insetY - itemH;
  const x = cellX2 > cellX1 ? seededRange(seedBase + 131, cellX1, cellX2) : effLeft + col * cellW + (cellW - itemW) / 2;
  const y = cellY2 > cellY1 ? seededRange(seedBase + 137, cellY1, cellY2) : effTop + row * cellH + (cellH - itemH) / 2;
  return {
    x: Math.max(effLeft, Math.min(x, effRight - itemW)),
    y: Math.max(effTop, Math.min(y, effBottom - itemH)),
  };
}

function planePoseChaos(i: number, vw: number, vh: number) {
  const seed = i * 7 + 1;
  const safe = avoidHero(seed, PLANE_GROUP_WIDTH, 60, vw, vh);
  return { x: safe.x, y: safe.y, scale: seededRange(i * 13 + 5, 0.85, 1.1), rotation: seededRange(i * 17 + 7, -35, 35), opacity: 1 };
}

function planePoseLeftStack(i: number, vw: number, vh: number) {
  const topBand = vh * STACK_TOP;
  const bottomBand = vh * STACK_BOT;
  const rowSpan = (bottomBand - topBand) / 7;
  const cardLeft = vw * 0.5 - PREFS_CARD_WIDTH / 2;
  return { x: cardLeft - PLANE_GAP - PLANE_GROUP_WIDTH, y: topBand + i * rowSpan, scale: 0.88, rotation: 0, opacity: 1 };
}

function planePoseReveal(i: number, vw: number, vh: number) {
  const topBand = vh * STACK_TOP;
  const bottomBand = vh * STACK_BOT;
  const rowSpan = (bottomBand - topBand) / 7;
  const cardRight = vw * 0.5 + PREFS_CARD_WIDTH / 2;
  return { x: cardRight + PLANE_GAP, y: topBand + i * rowSpan, scale: 0.95, rotation: 0, opacity: 1 };
}

function planePoseLand(i: number, planes: PlaneSpec[], vw: number, vh: number) {
  const spec = planes[i];
  if (!spec) return planePoseReveal(i, vw, vh);
  if (!spec.match) return { x: vw * 0.5 + PREFS_CARD_WIDTH, y: vh * 1.1, scale: 0.4, rotation: -15, opacity: 0 };
  const matchCount = planes.filter((p) => p.match).length;
  const matchIdx = planes.slice(0, i).filter((p) => p.match).length;
  const topBand = vh * 0.36;
  const bottomBand = vh * 0.82;
  const denom = Math.max(1, matchCount - 1);
  const rowSpan = (bottomBand - topBand) / denom;
  const phoneLeft = vw * 0.5 - PHONE_WIDTH / 2;
  return { x: phoneLeft - PLANE_GAP - PLANE_GROUP_WIDTH, y: topBand + matchIdx * rowSpan, scale: 0.9, rotation: 0, opacity: 1 };
}

function planePoseApply(i: number, planes: PlaneSpec[], vw: number, vh: number) {
  const spec = planes[i];
  if (!spec || !spec.match) return { x: vw * 0.5, y: vh * 1.2, scale: 0, rotation: 0, opacity: 0 };
  return { x: vw * 0.5 - PLANE_GROUP_WIDTH / 2, y: vh * 0.5 - 20, scale: 0.14, rotation: 0, opacity: 0 };
}

function notifPoseChaos(i: number, vw: number, vh: number) {
  const seed = i * 23 + 2;
  const safe = avoidHero(seed, 220, 70, vw, vh);
  return { x: safe.x, y: safe.y, scale: 1, rotation: seededRange(i * 31 + 6, -5, 5), opacity: 1 };
}

/* ── SVG templates ── */
const planeSVG = `<svg width="38" height="38" viewBox="0 0 40 40" fill="none"><defs><linearGradient id="hiw2-body" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#38b6ff"/><stop offset="60%" stop-color="#22d5d5"/><stop offset="100%" stop-color="#8d44f3"/></linearGradient></defs><path d="M2 20 L38 4 L24 38 L18 24 Z" fill="url(#hiw2-body)" stroke="#211b35" stroke-width="1" stroke-linejoin="round"/><path d="M18 24 L38 4" stroke="#211b35" stroke-width="1" stroke-linecap="round"/></svg>`;

const tickSVG = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="#0b1a1a" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const crossSVG = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6l-12 12" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round"/></svg>`;

function notifIconSVG(kind: string): string {
  if (kind === "call")
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z" fill="#38b6ff"/></svg>`;
  if (kind === "sms")
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z" fill="#ff9a1f"/></svg>`;
  return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2a10 10 0 0 0-8.5 15.1L2 22l5-1.4A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.7-.1-.4-.1-.9-.3-1.5-.6-2.6-1.1-4.3-3.7-4.4-3.9-.1-.2-1-1.4-1-2.7 0-1.3.7-1.9.9-2.2.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.4.2.5.7 1.7.8 1.8.1.1.1.3 0 .4-.1.2-.1.2-.3.4l-.3.4c-.1.1-.2.2-.1.4.1.2.6 1 1.3 1.6.9.8 1.6 1.1 1.9 1.2.2.1.3.1.5-.1l.8-1c.2-.2.3-.2.5-.1s1.3.6 1.5.7c.2.1.4.2.4.3.1.1.1.6-.1 1.2z" fill="#25d366"/></svg>`;
}

function notifIconBg(kind: string): string {
  if (kind === "call") return "rgba(56,182,255,0.18)";
  if (kind === "sms") return "rgba(255,154,31,0.18)";
  return "rgba(37,211,102,0.18)";
}

/* ── Component ── */
export default function HowItWorks() {
  const { t, hiwPlanes, hiwNotifs, isRTL } = useRegion();

  /* ── Refs: single elements ── */
  const sectionRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const titleBigRef = useRef<HTMLDivElement>(null);
  const stickyTitleRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const prefsCardRef = useRef<HTMLDivElement>(null);
  const scanSweepRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const jobCardRef = useRef<HTMLDivElement>(null);
  const appliedBadgeRef = useRef<HTMLDivElement>(null);
  const applyCaptionRef = useRef<HTMLDivElement>(null);

  /* ── Refs: arrays ── */
  const planeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const planeBodyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const labelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const notifRefs = useRef<(HTMLDivElement | null)[]>([]);
  const notifBodyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dotRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const copyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prefsTickRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const agencyRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const applyBtnRefs = useRef<(HTMLSpanElement | null)[]>([]);

  /* ── GSAP effect ── */
  useEffect(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 900px)", () => {
      const section = sectionRef.current;
      const pin = pinRef.current;
      if (!section || !pin) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const planeEls = planeRefs.current.filter(Boolean) as HTMLDivElement[];
      const planeBodyEls = planeBodyRefs.current.filter(Boolean) as HTMLDivElement[];
      const labelEls = labelRefs.current.filter(Boolean) as HTMLDivElement[];
      const badgeElsArr = badgeRefs.current.filter(Boolean) as HTMLDivElement[];
      const notifEls = notifRefs.current.filter(Boolean) as HTMLDivElement[];
      const notifBodyEls = notifBodyRefs.current.filter(Boolean) as HTMLDivElement[];
      const dots = dotRefs.current.filter(Boolean) as HTMLSpanElement[];
      const copies = copyRefs.current.filter(Boolean) as HTMLDivElement[];
      const prefsTicks = prefsTickRefs.current.filter(Boolean) as HTMLSpanElement[];
      const agencyRows = agencyRowRefs.current.filter(Boolean) as HTMLDivElement[];
      const applyBtns = applyBtnRefs.current.filter(Boolean) as HTMLSpanElement[];

      const titleBig = titleBigRef.current;
      const stickyTitle = stickyTitleRef.current;
      const stepper = stepperRef.current;
      const fill = fillRef.current;
      const prefsCard = prefsCardRef.current;
      const scanSweep = scanSweepRef.current;
      const phone = phoneRef.current;
      const jobCard = jobCardRef.current;
      const appliedBadge = appliedBadgeRef.current;
      const applyCaption = applyCaptionRef.current;

      /* ── Set initial state ── */
      planeEls.forEach((el, i) => {
        const p = planePoseChaos(i, vw, vh);
        gsap.set(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, opacity: p.opacity, transformOrigin: "center center" });
      });
      gsap.set(labelEls, { opacity: 0, scale: 0.6, transformOrigin: "left center" });
      gsap.set(badgeElsArr, { opacity: 0, scale: 0.6, transformOrigin: "center center" });

      notifEls.forEach((el, i) => {
        const p = notifPoseChaos(i, vw, vh);
        gsap.set(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, opacity: p.opacity, transformOrigin: "center center" });
      });

      gsap.set(prefsCard, { xPercent: -50, yPercent: -50, x: vw * 0.7, y: 120, opacity: 0 });
      gsap.set(prefsTicks, { opacity: 0, scale: 0 });
      gsap.set(scanSweep, { opacity: 0, xPercent: -100 });
      gsap.set(phone, { xPercent: -50, yPercent: -50, x: vw * 0.7, y: 100, opacity: 0 });
      gsap.set(jobCard, { xPercent: -50, yPercent: -50, opacity: 0, scale: 0.9, y: 130 });
      gsap.set(agencyRows, { opacity: 0, x: 60 });
      gsap.set(copies, { opacity: 0, y: 12 });
      if (copies[0]) gsap.set(copies[0], { opacity: 1, y: 0 });
      gsap.set(titleBig, { xPercent: -50, yPercent: -50, x: 0, y: 0, opacity: 1, scale: 1 });
      gsap.set(stickyTitle, { opacity: 0, y: -10 });
      gsap.set(stepper, { opacity: 0, y: -10 });

      /* ── Ambient chaos motion ── */
      const ambientTweens: gsap.core.Tween[] = [];
      planeBodyEls.forEach((body, i) => {
        const xAmp = 18 + ((i * 5) % 22);
        const yAmp = 14 + ((i * 7) % 18);
        const rotAmp = 12 + ((i * 3) % 14);
        const sclAmp = 0.06 + ((i * 2) % 5) * 0.02;
        ambientTweens.push(
          gsap.to(body, { x: "+=" + xAmp, duration: 2.6 + ((i * 0.37) % 2.2), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.11) % 1.5 }),
          gsap.to(body, { y: "+=" + yAmp, duration: 2.1 + ((i * 0.51) % 2.4), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.17) % 1.8 }),
          gsap.to(body, { rotation: "+=" + rotAmp, duration: 3.0 + ((i * 0.29) % 2.0), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.23) % 2.0 }),
          gsap.to(body, { scale: "+=" + sclAmp, duration: 3.4 + ((i * 0.43) % 1.8), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.19) % 1.6 }),
        );
      });
      notifBodyEls.forEach((body, i) => {
        ambientTweens.push(
          gsap.to(body, { x: "+=" + (6 + ((i * 3) % 10)), duration: 1.6 + ((i * 0.31) % 1.2), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.13) % 1.2 }),
          gsap.to(body, { y: "+=" + (5 + ((i * 4) % 9)), duration: 1.8 + ((i * 0.27) % 1.3), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.19) % 1.4 }),
          gsap.to(body, { rotation: "+=" + (2 + ((i * 2) % 4)), duration: 2.2 + ((i * 0.23) % 1.1), ease: "sine.inOut", yoyo: true, repeat: -1, delay: (i * 0.21) % 1.5 }),
        );
      });

      function killAmbient() {
        ambientTweens.forEach((tw) => tw.kill());
        gsap.set(planeBodyEls, { x: 0, y: 0, rotation: 0, scale: 1 });
        gsap.set(notifBodyEls, { x: 0, y: 0, rotation: 0 });
      }

      /* ── Master timeline ── */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "+=" + PX_PER_UNIT * PHASE_TIMES.total,
          pin: pin,
          pinSpacing: true,
          scrub: 0.8,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
        defaults: { ease: "power2.out" },
      });

      /* Big title -> sticky title handoff */
      tl.to(titleBig, { opacity: 0, scale: 0.8, y: -30, duration: 0.35 }, 0.05);
      tl.to(stickyTitle, { opacity: 1, y: 0, duration: 0.35 }, 0.15);
      tl.to(stepper, { opacity: 1, y: 0, duration: 0.2 }, 0.4);

      /* Stepper fill */
      if (fill) tl.to(fill, { width: "100%", duration: PHASE_TIMES.total, ease: "none" }, 0);
      const dotTimes = [PHASE_TIMES.chaosStart + 0.1, PHASE_TIMES.filterStart, PHASE_TIMES.revealStart, PHASE_TIMES.landStart, PHASE_TIMES.applyStart];
      dots.forEach((dot, i) => {
        if (dotTimes[i] !== undefined) {
          tl.to(dot, { backgroundColor: locumColors.teal, borderColor: locumColors.teal, color: locumColors.navy, duration: 0.15 }, dotTimes[i]);
        }
      });

      /* ── PHASE 1->2: filter ── */
      tl.to(copies[0], { opacity: 0, y: -12, duration: 0.25 }, PHASE_TIMES.filterStart - 0.2);
      tl.to(copies[1], { opacity: 1, y: 0, duration: 0.3 }, PHASE_TIMES.filterStart);
      tl.call(killAmbient, undefined, PHASE_TIMES.filterStart - 0.2);
      tl.to(notifEls, { scale: 0, opacity: 0, duration: 0.3, ease: "back.in(1.4)", stagger: 0.03 }, PHASE_TIMES.filterStart - 0.2);

      planeEls.forEach((el, i) => {
        const p = planePoseLeftStack(i, vw, vh);
        tl.to(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, duration: 0.55, ease: "power3.out" }, PHASE_TIMES.filterStart - 0.15 + i * 0.015);
      });
      tl.to(prefsCard, { x: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, PHASE_TIMES.filterStart);
      tl.to(prefsTicks, { scale: 1, opacity: 1, duration: 0.25, ease: "back.out(2)", stagger: 0.08 }, PHASE_TIMES.filterStart + 0.35);

      /* ── PHASE 2->3: reveal ── */
      tl.to(copies[1], { opacity: 0, y: -12, duration: 0.25 }, PHASE_TIMES.revealStart - 0.2);
      tl.to(copies[2], { opacity: 1, y: 0, duration: 0.3 }, PHASE_TIMES.revealStart);

      tl.to(scanSweep, { opacity: 1, duration: 0.15 }, PHASE_TIMES.revealStart - 0.15);
      tl.fromTo(scanSweep, { xPercent: -100 }, { xPercent: 100, duration: 1.0, ease: "power1.inOut" }, PHASE_TIMES.revealStart - 0.1);
      tl.to(scanSweep, { opacity: 0, duration: 0.2 }, PHASE_TIMES.revealStart + 0.95);

      const scanStart = PHASE_TIMES.revealStart - 0.05;
      const perPlaneDelay = 0.07;
      planeEls.forEach((el, i) => {
        const p = planePoseReveal(i, vw, vh);
        tl.to(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, duration: 0.55, ease: "power2.inOut" }, scanStart + i * perPlaneDelay);
      });
      labelEls.forEach((label, i) => {
        tl.to(label, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.6)" }, scanStart + i * perPlaneDelay + 0.32);
      });
      badgeElsArr.forEach((badge, i) => {
        tl.to(badge, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.9)" }, scanStart + i * perPlaneDelay + 0.42);
      });
      planeBodyEls.forEach((body, i) => {
        const spec = hiwPlanes[i];
        if (!spec) return;
        tl.to(body, { filter: spec.match ? "drop-shadow(0 0 10px rgba(34,213,213,0.67))" : "grayscale(1) brightness(0.6)", duration: 0.4 }, PHASE_TIMES.revealStart + 0.35);
      });

      /* ── PHASE 3->4: land ── */
      tl.to(copies[2], { opacity: 0, y: -12, duration: 0.25 }, PHASE_TIMES.landStart - 0.2);
      tl.to(copies[3], { opacity: 1, y: 0, duration: 0.3 }, PHASE_TIMES.landStart);
      tl.to(prefsCard, { x: -vw * 0.9, opacity: 0, duration: 0.55, ease: "power2.in" }, PHASE_TIMES.landStart - 0.1);

      const phoneEnterAt = PHASE_TIMES.landStart + 0.1;
      const phoneEnterDur = 0.7;
      tl.to(phone, { x: 0, opacity: 1, duration: phoneEnterDur, ease: "power3.out" }, phoneEnterAt);

      planeEls.forEach((el, i) => {
        const p = planePoseLand(i, hiwPlanes, vw, vh);
        tl.to(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, opacity: p.opacity, duration: phoneEnterDur, ease: "power3.out" }, phoneEnterAt);
      });
      hiwPlanes.forEach((spec, i) => {
        if (spec.match) return;
        if (labelEls[i]) tl.to(labelEls[i], { opacity: 0, duration: 0.35 }, phoneEnterAt);
        if (badgeElsArr[i]) tl.to(badgeElsArr[i], { opacity: 0, duration: 0.35 }, phoneEnterAt);
      });

      /* ── PHASE 4->5: apply ── */
      tl.to(copies[3], { opacity: 0, y: -12, duration: 0.25 }, PHASE_TIMES.applyStart - 0.2);
      tl.to(copies[4], { opacity: 1, y: 0, duration: 0.3 }, PHASE_TIMES.applyStart);

      const absorbStart = PHASE_TIMES.applyStart - 0.05;
      planeEls.forEach((el, i) => {
        const p = planePoseApply(i, hiwPlanes, vw, vh);
        const spec = hiwPlanes[i];
        const matchIdx = spec?.match ? hiwPlanes.slice(0, i).filter((pp) => pp.match).length : 0;
        const startAt = spec?.match ? absorbStart + matchIdx * 0.04 : absorbStart;
        tl.to(el, { x: p.x, y: p.y, scale: p.scale, rotation: p.rotation, opacity: p.opacity, duration: 0.45, ease: "power2.in" }, startAt);
        if (labelEls[i]) tl.to(labelEls[i], { opacity: 0, duration: 0.25 }, startAt);
        if (badgeElsArr[i]) tl.to(badgeElsArr[i], { opacity: 0, duration: 0.25 }, startAt);
      });

      tl.to(phone, { opacity: 0, scale: 0.95, duration: 0.45, ease: "power2.in" }, PHASE_TIMES.applyStart + 0.55);
      tl.to(jobCard, { opacity: 1, scale: 1, y: 100, duration: 0.55, ease: "power3.out" }, PHASE_TIMES.applyStart + 0.85);
      tl.to(agencyRows, { opacity: 1, x: 0, duration: 0.35, stagger: 0.07, ease: "power3.out" }, PHASE_TIMES.applyStart + 1.15);

      /* Phase 5 end sequence */
      const droppedRows = agencyRows.slice(1);
      droppedRows.forEach((el) => {
        el.style.overflow = "hidden";
      });
      droppedRows.forEach((row, i) => {
        tl.to(row, { opacity: 0, height: 0, marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0, borderWidth: 0, duration: 0.25, ease: "power2.inOut" }, 6.15 + i * 0.08);
      });
      if (applyCaption) {
        applyCaption.style.overflow = "hidden";
        tl.to(applyCaption, { opacity: 0, height: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0, duration: 0.2, ease: "power2.inOut" }, 6.4);
      }
      const btnPress = applyBtns[0];
      const firstRow = agencyRows[0];
      if (btnPress) {
        tl.to(btnPress, { scale: 0.88, backgroundColor: "#1aada0", color: "#ffffff", duration: 0.1, ease: "power2.in", transformOrigin: "center center" }, 6.65);
        tl.to(btnPress, { scale: 1, backgroundColor: locumColors.teal, color: locumColors.navy, duration: 0.12, ease: "back.out(2.2)" }, 6.78);
        tl.to(btnPress, { opacity: 0, duration: 0.12, ease: "power2.out" }, 7.0);
      }
      if (appliedBadge) {
        gsap.set(appliedBadge, { scale: 0.82 });
        tl.to(appliedBadge, { opacity: 1, scale: 1, duration: 0.16, ease: "back.out(2.4)", transformOrigin: "center center" }, 7.02);
      }
      if (firstRow) {
        tl.to(firstRow, { borderColor: "rgba(0,200,150,0.6)", boxShadow: "0 0 24px rgba(0,200,150,0.2), inset 0 0 0 1px rgba(0,200,150,0.33)", duration: 0.16, ease: "power2.out" }, 7.0);
      }

      return () => {
        tl.kill();
        ambientTweens.forEach((tw) => tw.kill());
      };
    });

    return () => mm.revert();
  }, [hiwPlanes, hiwNotifs, t]);

  /* ── Render ── */
  return (
    <Box
      component="section"
      id="hiw-section"
      ref={sectionRef}
      sx={{
        display: { xs: "none", md: "block" },
        position: "relative",
        background: `linear-gradient(180deg, #0f0a1f 0%, ${locumColors.navy} 50%, #0f0a1f 100%)`,
        overflow: "hidden",
        color: "#ffffff",
        scrollMarginTop: "72px",
      }}
    >
      <Box
        ref={pinRef}
        sx={{
          position: "relative",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          isolation: "isolate",
        }}
      >
        {/* Grid background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `linear-gradient(${locumColors.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${locumColors.gridLine} 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
            maskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
          }}
        />

        {/* Header band: stepper + phase copy */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            pt: "112px",
            pb: "24px",
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "18px",
          }}
        >
          {/* Stepper */}
          <Box
            ref={stepperRef}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: "22px",
              background: "rgba(15,10,31,0.7)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "999px",
              padding: "10px 22px",
            }}
          >
            <Box
              ref={fillRef}
              sx={{
                position: "absolute",
                ...(isRTL ? { right: 0 } : { left: 0 }),
                bottom: 0,
                height: "2px",
                width: 0,
                background: isRTL
                  ? `linear-gradient(270deg, ${locumColors.teal}, ${locumColors.purple})`
                  : `linear-gradient(90deg, ${locumColors.teal}, ${locumColors.purple})`,
                borderRadius: "0 0 999px 999px",
              }}
            />
            {STEPPER_LABELS.map((key, i) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box
                  component="span"
                  ref={(el: HTMLSpanElement | null) => {
                    dotRefs.current[i] = el;
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: "1.5px solid rgba(255,255,255,0.35)",
                    background: "transparent",
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "11px",
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  {i + 1}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.85)",
                    whiteSpace: "nowrap",
                    display: { xs: "none", lg: "inline" },
                  }}
                >
                  {t(key)}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Sticky title (replaces big title after phase 0) */}
          <Box
            ref={stickyTitleRef}
            sx={{
              position: "relative",
              textAlign: "center",
              width: "min(720px, calc(100% - 32px))",
              minHeight: "96px",
              px: "16px",
            }}
          >
            {PHASE_HEADINGS.map((hKey, i) => (
              <Box
                key={hKey}
                ref={(el: HTMLDivElement | null) => {
                  copyRefs.current[i] = el;
                }}
                sx={{ position: "absolute", left: 0, right: 0, top: 0 }}
              >
                <Typography
                  sx={{
                    fontSize: "26px",
                    fontWeight: 800,
                    color: "#ffffff",
                    mb: "6px",
                  }}
                >
                  {t(hKey)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                    maxWidth: 560,
                    mx: "auto",
                  }}
                >
                  {t(PHASE_SUBS[i])}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Big centre title (hero, visible at time 0) */}
        <Box
          ref={titleBigRef}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            pointerEvents: "none",
            willChange: "transform, opacity",
            px: "24px",
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              padding: "6px 14px",
              borderRadius: "100px",
              background: locumColors.tealBgSubtle,
              border: `1px solid ${locumColors.tealBorderMid}`,
              fontSize: "11px",
              fontWeight: 700,
              color: locumColors.teal,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              mb: "24px",
            }}
          >
            {t("hiw-pill")}
          </Box>
          <Typography
            component="h2"
            sx={{
              fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "#ffffff",
              lineHeight: 1.1,
              m: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "14px",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-block",
                padding: "4px 16px",
                borderRadius: "6px",
                background: locumColors.pink,
                color: "#0b0b0d",
              }}
            >
              {t("hiw-hl1")}
            </Box>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                padding: "4px 16px",
                borderRadius: "6px",
                background: locumColors.teal,
                color: "#0b0b0d",
              }}
            >
              {t("hiw-hl2")}
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: "24px",
              fontSize: "16px",
              fontWeight: 500,
              color: "rgba(255,255,255,0.7)",
              maxWidth: 520,
            }}
          >
            {t("hiw-scroll-hint")}
          </Typography>
        </Box>

        {/* Notifications layer */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 }}>
          {hiwNotifs.map((n: HiwNotifSpec, i: number) => (
            <Box
              key={n.id}
              ref={(el: HTMLDivElement | null) => {
                notifRefs.current[i] = el;
              }}
              sx={{ position: "absolute", top: 0, left: 0, willChange: "transform, opacity" }}
            >
              <Box
                ref={(el: HTMLDivElement | null) => {
                  notifBodyRefs.current[i] = el;
                }}
                sx={{
                  width: 220,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "14px",
                  background: "rgba(15,10,31,0.86)",
                  backdropFilter: "blur(14px)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  boxShadow: "0 14px 30px rgba(0,0,0,0.45)",
                  willChange: "transform",
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: notifIconBg(n.kind),
                  }}
                  dangerouslySetInnerHTML={{ __html: notifIconSVG(n.kind) }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: "11px", fontWeight: 800, color: "#ffffff", lineHeight: 1.2 }}>{n.sender}</Box>
                  <Box
                    sx={{
                      fontSize: "10.5px",
                      color: "rgba(255,255,255,0.65)",
                      lineHeight: 1.3,
                      mt: "2px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {n.text}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Planes layer */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3 }}>
          {hiwPlanes.map((spec: PlaneSpec, i: number) => (
            <Box
              key={spec.id}
              ref={(el: HTMLDivElement | null) => {
                planeRefs.current[i] = el;
              }}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                willChange: "transform, opacity",
              }}
            >
              <Box
                ref={(el: HTMLDivElement | null) => {
                  planeBodyRefs.current[i] = el;
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  willChange: "transform",
                }}
                dangerouslySetInnerHTML={{ __html: planeSVG }}
              />
              <Box
                ref={(el: HTMLDivElement | null) => {
                  labelRefs.current[i] = el;
                }}
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "5px 10px",
                  borderRadius: "999px",
                  whiteSpace: "nowrap",
                  backdropFilter: "blur(6px)",
                  letterSpacing: "0.01em",
                  ...(spec.match
                    ? {
                        background: "rgba(34,213,213,0.13)",
                        color: locumColors.teal,
                        border: "1px solid rgba(34,213,213,0.4)",
                      }
                    : {
                        background: "rgba(255,255,255,0.05)",
                        color: "rgba(255,255,255,0.55)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        textDecoration: "line-through",
                        textDecorationColor: "rgba(255,255,255,0.3)",
                      }),
                }}
              >
                {spec.label}
              </Box>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  badgeRefs.current[i] = el;
                }}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ...(spec.match
                    ? { background: locumColors.teal, boxShadow: `0 0 10px ${locumColors.tealGlow}` }
                    : { background: "#e8425a", boxShadow: "0 0 10px rgba(232,66,90,0.53)" }),
                }}
                dangerouslySetInnerHTML={{ __html: spec.match ? tickSVG : crossSVG }}
              />
            </Box>
          ))}
        </Box>

        {/* Scan sweep overlay */}
        <Box
          ref={scanSweepRef}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "100%",
            zIndex: 5,
            pointerEvents: "none",
            background: `linear-gradient(90deg, transparent 0%, rgba(34,213,213,0) 20%, rgba(34,213,213,0.33) 48%, rgba(141,68,243,0.4) 50%, rgba(34,213,213,0.33) 52%, rgba(34,213,213,0) 80%, transparent 100%)`,
            mixBlendMode: "screen",
            filter: "blur(8px)",
          }}
        />

        {/* Preferences card */}
        <Box
          ref={prefsCardRef}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 30,
            width: PREFS_CARD_WIDTH,
            borderRadius: "18px",
            background: "rgba(15,10,31,0.88)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(34,213,213,0.4)",
            boxShadow: "0 0 50px rgba(34,213,213,0.19)",
            padding: "22px 26px",
            willChange: "transform, opacity",
          }}
        >
          {/* Prefs header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, #38b6ff, ${locumColors.purple})`,
                }}
              />
              <Box component="span" sx={{ fontSize: "15px", fontWeight: 800, color: "#ffffff", letterSpacing: "-0.01em" }}>
                Locum<Box component="span" sx={{ color: locumColors.teal }}>+</Box>
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "9.5px",
                fontWeight: 800,
                letterSpacing: "0.12em",
                color: locumColors.teal,
                border: `1px solid ${locumColors.tealGlow}`,
                borderRadius: "6px",
                padding: "3px 8px",
              }}
            >
              {t("hiw-prefs-badge")}
            </Box>
          </Box>
          <Box sx={{ fontSize: "19px", fontWeight: 800, color: "#ffffff", mb: "16px" }}>{t("hiw-prefs-title")}</Box>
          {/* Prefs rows */}
          {[
            { lKey: "hiw-prefs-l-grade", vKey: "hiw-prefs-v-grade" },
            { lKey: "hiw-prefs-l-spec", vKey: "hiw-prefs-v-spec" },
            { lKey: "hiw-prefs-l-loc", vKey: "hiw-prefs-loc" },
            { lKey: "hiw-prefs-l-rate", vKey: "hiw-prefs-rate" },
            { lKey: "hiw-prefs-l-shifts", vKey: "hiw-prefs-v-shifts", last: true },
          ].map((row, ri) => (
            <Box
              key={row.lKey}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: "9px",
                borderBottom: row.last ? "none" : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Box component="span" sx={{ fontSize: "12px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>
                {t(row.lKey)}
              </Box>
              <Box component="span" sx={{ fontSize: "12.5px", color: "#ffffff", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                {t(row.vKey)}
                <Box
                  component="span"
                  ref={(el: HTMLSpanElement | null) => {
                    prefsTickRefs.current[ri] = el;
                  }}
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "5px",
                    background: locumColors.teal,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "#0b1a1a",
                    boxShadow: `0 0 10px ${locumColors.tealGlow}`,
                  }}
                >
                  &#10003;
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Phone mockup (phase 4) */}
        <Box
          ref={phoneRef}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 30,
            width: PHONE_WIDTH,
            willChange: "transform, opacity",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              borderRadius: "38px",
              background: "#1a1528",
              p: "8px",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.08), 0 25px 60px rgba(0,0,0,0.4)",
            }}
          >
            <Box sx={{ borderRadius: "32px", overflow: "hidden", background: "#0d0a18", display: "flex", flexDirection: "column", height: 520 }}>
              {/* Status bar */}
              <Box sx={{ px: "22px", pt: "10px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>
                <span>9:41</span>
                <Box sx={{ width: 72, height: 20, background: "#000", borderRadius: "10px" }} />
                <Box sx={{ width: 18, height: 8, border: "1px solid rgba(255,255,255,0.4)", borderRadius: "2px", p: "1px" }}>
                  <Box sx={{ width: "75%", height: "100%", background: locumColors.teal, borderRadius: "1px" }} />
                </Box>
              </Box>
              {/* Feed */}
              <Box sx={{ flex: 1, px: "18px", pt: "16px", pb: "14px", overflow: "hidden" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "14px" }}>
                  <Box component="span" sx={{ fontSize: "14px", fontWeight: 800, color: "#fff" }}>{t("hiw-feed-title")}</Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "9px",
                      fontWeight: 700,
                      color: locumColors.teal,
                      border: "1px solid rgba(34,213,213,0.5)",
                      borderRadius: "5px",
                      padding: "2px 7px",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {t("hiw-feed-matches")}
                  </Box>
                </Box>
                {/* Primary card */}
                <Box sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", p: "14px 16px", mb: "10px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "10px" }}>
                    <Box component="span" sx={{ fontSize: "8px", fontWeight: 800, color: locumColors.teal, border: "1px solid rgba(34,213,213,0.5)", borderRadius: "4px", padding: "2px 6px", letterSpacing: "0.1em" }}>
                      {t("hiw-match-badge")}
                    </Box>
                    <Box component="span" sx={{ fontSize: "8px", fontWeight: 700, color: locumColors.success }}>{t("hiw-phone-verified")}</Box>
                  </Box>
                  <Box sx={{ fontSize: "12px", fontWeight: 800, color: "#fff", mb: "8px" }}>{t("hiw-phone-title")}</Box>
                  <Box sx={{ display: "flex", gap: "4px", mb: "10px" }}>
                    <SkeletonChip w={36} />
                    <SkeletonChip w={44} />
                    <SkeletonChip w={30} />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: "6px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <Box component="span" sx={{ fontSize: "10px", fontWeight: 800, color: "rgba(141,68,243,0.85)" }}>{t("hiw-phone-rate")}</Box>
                    <Box component="span" sx={{ fontSize: "8.5px", fontWeight: 800, letterSpacing: "0.08em", color: locumColors.navy, background: locumColors.teal, borderRadius: "6px", padding: "4px 10px" }}>
                      {t("hiw-view-agencies")}
                    </Box>
                  </Box>
                </Box>
                {/* Skeleton cards */}
                <Box sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", p: "11px 13px", mb: "10px", opacity: 0.5 }}>
                  <Box sx={{ fontSize: "11px", fontWeight: 800, color: "#fff", mb: "4px" }}>{t("hiw-phone-card2")}</Box>
                  <SkeletonLine w="70%" />
                </Box>
                <Box sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", p: "11px 13px", opacity: 0.35 }}>
                  <Box sx={{ fontSize: "11px", fontWeight: 800, color: "#fff", mb: "4px" }}>{t("hiw-phone-card3")}</Box>
                  <SkeletonLine w="60%" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Job detail / Apply card (phase 5) */}
        <Box
          ref={jobCardRef}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 35,
            width: PHONE_WIDTH,
            willChange: "transform, opacity",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              borderRadius: "38px",
              background: "#1a1528",
              p: "8px",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.08), 0 25px 60px rgba(0,0,0,0.4)",
            }}
          >
            <Box sx={{ borderRadius: "32px", overflow: "hidden", background: "#0d0a18", display: "flex", flexDirection: "column", height: 520 }}>
              {/* Status bar */}
              <Box sx={{ px: "22px", pt: "10px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>
                <span>9:41</span>
                <Box sx={{ width: 72, height: 20, background: "#000", borderRadius: "10px" }} />
                <Box sx={{ width: 18, height: 8, border: "1px solid rgba(255,255,255,0.4)", borderRadius: "2px", p: "1px" }}>
                  <Box sx={{ width: "75%", height: "100%", background: locumColors.teal, borderRadius: "1px" }} />
                </Box>
              </Box>
              {/* Apply content */}
              <Box sx={{ flex: 1, px: "22px", pt: "16px", pb: "14px", overflow: "hidden", background: "#0d0a18" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "12px" }}>
                  <Box component="span" sx={{ fontSize: "18px", color: "rgba(255,255,255,0.75)" }}>&#8249;</Box>
                  <Box component="span" sx={{ fontSize: "14px", fontWeight: 800, color: "#fff" }}>{t("hiw-apply-title")}</Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "8px" }}>
                  <Box component="span" sx={{ fontSize: "8.5px", fontWeight: 800, color: locumColors.teal, border: "1px solid rgba(34,213,213,0.5)", borderRadius: "5px", padding: "3px 7px", letterSpacing: "0.12em" }}>
                    {t("hiw-job-agencies")}
                  </Box>
                  <Box component="span" sx={{ fontSize: "8.5px", fontWeight: 700, color: locumColors.success, display: "flex", alignItems: "center", gap: "3px" }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: locumColors.success, boxShadow: `0 0 6px ${locumColors.success}`, display: "inline-block" }} />
                    {t("hiw-verified")}
                  </Box>
                </Box>
                <Box sx={{ fontSize: "15px", fontWeight: 900, color: "#fff", lineHeight: 1.2, mb: "6px" }}>{t("hiw-job-title")}</Box>
                <SkeletonLine w="62%" h={7} mb={9} />
                <Box sx={{ display: "flex", gap: "6px", mb: "18px" }}>
                  <SkeletonChip w={38} />
                  <SkeletonChip w={46} />
                  <SkeletonChip w={32} />
                </Box>
                <Box sx={{ fontSize: "9px", fontWeight: 700, color: "rgba(255,255,255,0.45)", letterSpacing: "0.1em", mb: "8px" }}>{t("hiw-apply-via")}</Box>

                {/* Agency rows */}
                {[
                  { badgeKey: "hiw-badge-verified", badgeBg: locumColors.teal, badgeColor: locumColors.navy, rateKey: "hiw-ag0-rate", nameKey: "hiw-ag0-name", hasApplied: true },
                  { badgeKey: "hiw-badge-trusted", badgeBg: "#38b6ff", badgeColor: "#fff", rateKey: "hiw-ag1-rate", nameKey: "hiw-ag1-name", hasApplied: false },
                  { badgeKey: "hiw-badge-fastest", badgeBg: locumColors.amber, badgeColor: locumColors.navy, rateKey: "hiw-ag2-rate", nameKey: "hiw-ag2-name", hasApplied: false },
                ].map((ag, ai) => (
                  <Box
                    key={ag.nameKey}
                    ref={(el: HTMLDivElement | null) => {
                      agencyRowRefs.current[ai] = el;
                    }}
                    sx={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "12px",
                      p: "11px 13px",
                      mb: "11px",
                      willChange: "transform, opacity",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          fontSize: "7.5px",
                          fontWeight: 800,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          borderRadius: "999px",
                          padding: "2px 6px",
                          lineHeight: 1.4,
                          background: ag.badgeBg,
                          color: ag.badgeColor,
                        }}
                      >
                        {t(ag.badgeKey)}
                      </Box>
                      <Box component="span" sx={{ fontSize: "10px", fontWeight: 800, color: "rgba(141,68,243,0.85)" }}>{t(ag.rateKey)}</Box>
                    </Box>
                    <Box sx={{ fontSize: "11px", fontWeight: 800, color: "#fff", mb: "2px" }}>{t(ag.nameKey)}</Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "7px" }}>
                      <Box>
                        <SkeletonLine w={56} h={5} mb={4} />
                        <SkeletonLine w={36} h={5} />
                      </Box>
                      {ag.hasApplied ? (
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <Box
                            component="span"
                            ref={(el: HTMLSpanElement | null) => {
                              applyBtnRefs.current[ai] = el;
                            }}
                            sx={{
                              fontSize: "8.5px",
                              fontWeight: 800,
                              letterSpacing: "0.08em",
                              color: locumColors.navy,
                              background: locumColors.teal,
                              borderRadius: "6px",
                              padding: "4px 10px",
                              willChange: "transform, opacity",
                            }}
                          >
                            INSTANT APPLY
                          </Box>
                          <Box
                            ref={appliedBadgeRef}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              fontSize: "8.5px",
                              fontWeight: 800,
                              letterSpacing: "0.08em",
                              color: "#ffffff",
                              background: locumColors.success,
                              borderRadius: "6px",
                              padding: "4px 10px",
                              opacity: 0,
                              pointerEvents: "none",
                              willChange: "transform, opacity",
                              boxShadow: "0 0 14px rgba(0,200,150,0.4)",
                            }}
                          >
                            &#10003; APPLIED
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          component="span"
                          ref={(el: HTMLSpanElement | null) => {
                            applyBtnRefs.current[ai] = el;
                          }}
                          sx={{
                            fontSize: "8.5px",
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            color: locumColors.navy,
                            background: locumColors.teal,
                            borderRadius: "6px",
                            padding: "4px 10px",
                            willChange: "transform, opacity",
                          }}
                        >
                          INSTANT APPLY
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
                <Box
                  ref={applyCaptionRef}
                  sx={{
                    fontSize: "8.5px",
                    color: "rgba(255,255,255,0.5)",
                    mt: "10px",
                    textAlign: "center",
                    fontStyle: "italic",
                    overflow: "hidden",
                  }}
                >
                  {t("hiw-apply-caption")}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ── Skeleton helpers ── */
function SkeletonChip({ w }: { w: number }) {
  return (
    <Box
      component="span"
      sx={{
        height: 10,
        width: w,
        borderRadius: "5px",
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.05)",
        display: "inline-block",
      }}
    />
  );
}

function SkeletonLine({ w, h = 6, mb = 0 }: { w: number | string; h?: number; mb?: number }) {
  return (
    <Box
      sx={{
        height: h,
        width: w,
        borderRadius: h / 2,
        background: "linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.08) 100%)",
        mb: mb ? `${mb}px` : 0,
      }}
    />
  );
}
